import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const RegisterForm = ({ onSwitchToLogin, onRegisterSuccess }) => {
  const { register, loading, error, clearError } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('One number');
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push('One special character');
    }
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation errors for the field being updated
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }

    // Real-time password validation
    if (name === 'password') {
      const passwordErrors = validatePassword(value);
      if (passwordErrors.length > 0) {
        setValidationErrors(prev => ({
          ...prev,
          password: passwordErrors
        }));
      }
    }

    // Real-time confirm password validation
    if (name === 'confirmPassword' || (name === 'password' && formData.confirmPassword)) {
      const password = name === 'password' ? value : formData.password;
      const confirmPassword = name === 'confirmPassword' ? value : formData.confirmPassword;
      
      if (confirmPassword && password !== confirmPassword) {
        setValidationErrors(prev => ({
          ...prev,
          confirmPassword: ['Passwords do not match']
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = ['First name is required'];
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = ['Last name is required'];
    }
    
    if (!formData.email.trim()) {
      errors.email = ['Email is required'];
    }
    
    if (!formData.inviteCode.trim()) {
      errors.inviteCode = ['Invite code is required'];
    }
    
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      errors.password = passwordErrors;
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = ['Passwords do not match'];
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    try {
      const { confirmPassword, ...registrationData } = formData;
      await register(registrationData);
      if (onRegisterSuccess) {
        onRegisterSuccess();
      }
      // Navigate to dashboard after successful registration
      navigate('/dashboard');
    } catch (error) {
      // Error is handled by AuthContext
      console.error('Registration failed:', error.message);
    }
  };

  return (
    <div>
      {error && (
        <div className="mb-6 p-4 glass-alert-error rounded-lg animate-slide-in-up">
          <p className={`text-mono text-sm ${isDark ? 'text-red-300' : 'text-red-600'}`}>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className={`block text-mono mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 glass-input rounded-lg text-mono placeholder-gray-400 focus:outline-none"
              placeholder="First name"
            />
            {validationErrors.firstName && (
              <p className={`text-mono text-xs mt-2 ${isDark ? 'text-red-300' : 'text-red-600'}`}>{validationErrors.firstName[0]}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastName" className={`block text-mono mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 glass-input rounded-lg text-mono placeholder-gray-400 focus:outline-none"
              placeholder="Last name"
            />
            {validationErrors.lastName && (
              <p className={`text-mono text-xs mt-2 ${isDark ? 'text-red-300' : 'text-red-600'}`}>{validationErrors.lastName[0]}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="inviteCode" className={`block text-mono mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Invite Code
          </label>
          <input
            type="text"
            id="inviteCode"
            name="inviteCode"
            value={formData.inviteCode}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 glass-input rounded-lg text-mono placeholder-gray-400 focus:outline-none uppercase"
            placeholder="EYE-XXXXXXXX"
            pattern="^[A-Z]{3}-[A-Z0-9]{8}$"
          />
          {validationErrors.inviteCode && (
            <p className={`text-mono text-xs mt-2 ${isDark ? 'text-red-300' : 'text-red-600'}`}>{validationErrors.inviteCode[0]}</p>
          )}
          <p className={`text-mono text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Enter your invite code to create an account
          </p>
        </div>

        <div>
          <label htmlFor="email" className={`block text-mono mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 glass-input rounded-lg text-mono placeholder-gray-400 focus:outline-none"
            placeholder="Enter your email"
          />
          {validationErrors.email && (
            <p className={`text-mono text-xs mt-2 ${isDark ? 'text-red-300' : 'text-red-600'}`}>{validationErrors.email[0]}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className={`block text-mono mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 glass-input rounded-lg text-mono placeholder-gray-400 focus:outline-none pr-12"
              placeholder="Create a password"
            />
            <button
              type="button"
              className={`absolute inset-y-0 right-0 pr-4 flex items-center transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464m1.414 1.414L8.464 8.464m5.656 5.656l1.415 1.415m-1.415-1.415l1.415 1.415M14.122 14.122L8.464 8.464" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          {validationErrors.password && (
            <div className="glass-alert-warning rounded-lg p-3 mt-2">
              <p className={`text-mono text-xs mb-1 ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>Password must contain:</p>
              <ul className={`list-disc list-inside ml-2 text-mono text-xs ${isDark ? 'text-yellow-200' : 'text-yellow-600'}`}>
                {validationErrors.password.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className={`block text-mono mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Confirm Password
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 glass-input rounded-lg text-mono placeholder-gray-400 focus:outline-none pr-12"
              placeholder="Confirm your password"
            />
            <button
              type="button"
              className={`absolute inset-y-0 right-0 pr-4 flex items-center transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464m1.414 1.414L8.464 8.464m5.656 5.656l1.415 1.415m-1.415-1.415l1.415 1.415M14.122 14.122L8.464 8.464" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          {validationErrors.confirmPassword && (
            <p className={`text-mono text-xs mt-2 ${isDark ? 'text-red-300' : 'text-red-600'}`}>{validationErrors.confirmPassword[0]}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full btn-glass-accent py-3 px-6 rounded-lg text-mono font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="glass-spinner w-5 h-5 mr-3"></div>
              Creating account...
            </div>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      <div className="mt-8 text-center">
        <p className={`text-mono ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-accent-400 hover:text-accent-300 font-medium transition-colors"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

// Wrapper component with blur background
export const RegisterFormWithBlur = ({ ...props }) => {
  const [showBlur, setShowBlur] = useState(false);
  
  useEffect(() => {
    // Trigger blur animation after component mount
    const timer = setTimeout(() => setShowBlur(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <>
      <div className={`dashboard-blur-bg ${showBlur ? 'transitioning' : ''}`} />
      <RegisterForm {...props} />
    </>
  );
};

export default RegisterForm;